/**
 * Schedules Page
 * Manage driver availability and working hours
 */

import { useState, useCallback, useMemo } from 'react';
import { Button, Card, Badge, Input } from '../components/ui';
import {
  WeeklyScheduleGrid,
  ScheduleExceptionForm,
  AvailabilityCalendar,
} from '../components/schedules';
import {
  useSchedules,
  useDrivers,
  useCreateSchedule,
  useUpdateSchedule,
  useAddException,
  useDeleteSchedule,
  useZones,
} from '../hooks/useApi';
import { Loader2, AlertCircle, Plus, User, Clock, Calendar, CheckCircle, XCircle, Search, Trash2, MapPin, Snowflake } from 'lucide-react';
import { cn } from '../utils/cn';
import type { WeeklyTimeSlot, ScheduleException, DriverSchedule, Zone } from '../types';
import { useCan } from '../contexts/useAuth';

/** Mirrors the backend whitelist in `types/routing.types.ts`. */
const COLD_CHAIN_CAPABILITY = 'cold_chain';

// Day mapping from JS day index to our DayOfWeek type
const DAY_MAPPING = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// Helper to check if driver is available today
const isAvailableToday = (schedule: DriverSchedule | undefined): boolean => {
  if (!schedule) return false;
  const today = new Date();
  const todayDay = DAY_MAPPING[today.getDay()];
  const todaySlot = schedule.weekly_template?.find(
    (slot) => slot.day === todayDay && slot.is_available
  );
  return !!todaySlot;
};

// Get working hours for today
const getTodayHours = (schedule: DriverSchedule | undefined): string => {
  if (!schedule) return 'No schedule';
  const today = new Date();
  const todayDay = DAY_MAPPING[today.getDay()];
  const todaySlot = schedule.weekly_template?.find(
    (slot) => slot.day === todayDay && slot.is_available
  );
  if (!todaySlot) return 'Off today';
  return `${todaySlot.start} - ${todaySlot.end}`;
};

// Count working days
const getWorkingDays = (schedule: DriverSchedule | undefined): number => {
  if (!schedule) return 0;
  return schedule.weekly_template?.filter(s => s.is_available).length || 0;
};

export default function Schedules() {
  const can = useCan();
  const [selectedDriverUid, setSelectedDriverUid] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyWithSchedule, setShowOnlyWithSchedule] = useState(false);

  // Queries
  const {
    data: schedules,
    isLoading: isLoadingSchedules,
    isError: isSchedulesError,
    refetch: refetchSchedules,
  } = useSchedules();

  const {
    data: drivers,
    isLoading: isLoadingDrivers,
    isError: isDriversError,
  } = useDrivers();

  // Fetch zones for zone assignment (using Baghdad city_id = 1)
  const { data: zones } = useZones(1);

  // Mutations
  const createScheduleMutation = useCreateSchedule();
  const updateScheduleMutation = useUpdateSchedule();
  const addExceptionMutation = useAddException();
  const deleteScheduleMutation = useDeleteSchedule();

  // Create a map of driver_uid to schedule
  const scheduleMap = useMemo(() => {
    const map = new Map<string, DriverSchedule>();
    schedules?.forEach(s => map.set(s.driver_uid, s));
    return map;
  }, [schedules]);

  // Combine drivers with their schedules
  const driversWithSchedules = useMemo(() => {
    if (!drivers) return [];
    return drivers.map(driver => ({
      driver,
      schedule: scheduleMap.get(driver.driver_uid),
    }));
  }, [drivers, scheduleMap]);

  // Filter and sort drivers based on search and schedule status
  const filteredDrivers = useMemo(() => {
    const filtered = driversWithSchedules.filter(({ driver, schedule }) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = driver.full_name?.toLowerCase().includes(query);
        const matchesUid = driver.driver_uid.toLowerCase().includes(query);
        if (!matchesName && !matchesUid) return false;
      }
      // Schedule filter
      if (showOnlyWithSchedule && !schedule) return false;
      return true;
    });

    // Sort: Drivers with schedules first, then alphabetically by name
    return filtered.sort((a, b) => {
      const hasA = !!a.schedule;
      const hasB = !!b.schedule;

      if (hasA && !hasB) return -1;
      if (!hasA && hasB) return 1;

      // Secondary sort by name
      const nameA = a.driver.full_name || a.driver.driver_uid;
      const nameB = b.driver.full_name || b.driver.driver_uid;
      return nameA.localeCompare(nameB);
    });
  }, [driversWithSchedules, searchQuery, showOnlyWithSchedule]);

  // Get selected driver and schedule
  const selectedDriver = drivers?.find(d => d.driver_uid === selectedDriverUid);
  const selectedSchedule = selectedDriverUid ? scheduleMap.get(selectedDriverUid) : undefined;

  const isLoading = isLoadingSchedules || isLoadingDrivers;
  const isError = isSchedulesError || isDriversError;

  // Handle create schedule for a driver
  const handleCreateSchedule = useCallback(
    async (driverUid: string) => {
      try {
        await createScheduleMutation.mutateAsync({ driverUid });
        refetchSchedules();
        setSelectedDriverUid(driverUid);
      } catch (error) {
        console.error('Failed to create schedule:', error);
      }
    },
    [createScheduleMutation, refetchSchedules]
  );

  // Handle weekly schedule save
  const handleSaveWeeklySchedule = useCallback(
    async (weeklyTemplate: WeeklyTimeSlot[]) => {
      if (!selectedDriverUid) return;

      // If no schedule exists, create one first
      if (!selectedSchedule) {
        await createScheduleMutation.mutateAsync({
          driverUid: selectedDriverUid,
          weeklyTemplate,
        });
      } else {
        await updateScheduleMutation.mutateAsync({
          driverUid: selectedDriverUid,
          params: { weeklyTemplate },
        });
      }
      refetchSchedules();
    },
    [selectedDriverUid, selectedSchedule, createScheduleMutation, updateScheduleMutation, refetchSchedules]
  );

  // Handle add exception
  const handleAddException = useCallback(
    async (exception: ScheduleException) => {
      if (!selectedDriverUid) return;

      // If no schedule exists, create one first
      if (!selectedSchedule) {
        await createScheduleMutation.mutateAsync({ driverUid: selectedDriverUid });
      }

      await addExceptionMutation.mutateAsync({
        driverUid: selectedDriverUid,
        exception: {
          date: exception.date,
          start: exception.start,
          end: exception.end,
          reason: exception.reason,
        },
      });
      refetchSchedules();
    },
    [selectedDriverUid, selectedSchedule, createScheduleMutation, addExceptionMutation, refetchSchedules]
  );

  // Handle remove exception
  const handleRemoveException = useCallback(
    async () => {
      if (!selectedSchedule) return;
      console.log('Remove exception not implemented yet');
      refetchSchedules();
    },
    [selectedSchedule, refetchSchedules]
  );

  // Handle delete schedule
  const handleDeleteSchedule = useCallback(
    async () => {
      if (!selectedDriverUid || !selectedSchedule) return;

      if (!window.confirm('Are you sure you want to delete this schedule? This action cannot be undone.')) {
        return;
      }

      try {
        await deleteScheduleMutation.mutateAsync(selectedDriverUid);
        refetchSchedules();
      } catch (error) {
        console.error('Failed to delete schedule:', error);
      }
    },
    [selectedDriverUid, selectedSchedule, deleteScheduleMutation, refetchSchedules]
  );

  // Handle zone toggle
  const handleZoneToggle = useCallback(
    async (zoneId: string) => {
      if (!selectedDriverUid || !selectedSchedule) return;

      const currentZones = selectedSchedule.zone_ids || [];
      const newZones = currentZones.includes(zoneId)
        ? currentZones.filter(id => id !== zoneId)
        : [...currentZones, zoneId];

      try {
        await updateScheduleMutation.mutateAsync({
          driverUid: selectedDriverUid,
          params: { zoneIds: newZones },
        });
        refetchSchedules();
      } catch (error) {
        console.error('Failed to update zones:', error);
      }
    },
    [selectedDriverUid, selectedSchedule, updateScheduleMutation, refetchSchedules]
  );

  // Vehicle capabilities. Same instant-save shape as the zone toggles, and the
  // only place a refrigerated van gets named: nothing in Core identifies one,
  // so until this is set the cold-chain assignment guard stays inert and every
  // driver is offered for a cold route.
  const handleCapabilityToggle = useCallback(
    async (capability: string) => {
      if (!selectedDriverUid || !selectedSchedule) return;

      const current = selectedSchedule.capabilities || [];
      const next = current.includes(capability)
        ? current.filter((c) => c !== capability)
        : [...current, capability];

      try {
        await updateScheduleMutation.mutateAsync({
          driverUid: selectedDriverUid,
          params: { capabilities: next },
        });
        refetchSchedules();
      } catch (error) {
        console.error('Failed to update capabilities:', error);
      }
    },
    [selectedDriverUid, selectedSchedule, updateScheduleMutation, refetchSchedules]
  );

  /**
   * Home zone label (PRD §8). Free text, and deliberately NOT a zone — a label
   * that is not a Zone document cannot reach the solver's zone→skill mapper by
   * any path, which is what makes the PRD's Hard Constraint ("no task may ever
   * require a TempZone skill") structurally true rather than policy-enforced.
   */
  const handleSaveHomeZoneLabel = useCallback(
    async (label: string) => {
      if (!selectedDriverUid || !selectedSchedule) return;
      if ((selectedSchedule.home_zone_label ?? '') === label.trim()) return; // no-op edits stay silent

      try {
        await updateScheduleMutation.mutateAsync({
          driverUid: selectedDriverUid,
          params: { homeZoneLabel: label.trim() || null },
        });
        refetchSchedules();
      } catch (error) {
        console.error('Failed to update home zone label:', error);
      }
    },
    [selectedDriverUid, selectedSchedule, updateScheduleMutation, refetchSchedules]
  );

  /**
   * Vehicle capacity. Rendered as read-only text until now, even though the API
   * always accepted it — so every prod schedule still sits at the 400 it was
   * created with while the platform default moved to 600, and 400 is what
   * actually binds. Editable per driver rather than bulk-migrated: this is the
   * per-route ORDER CEILING for every shift that driver works, not a cold-van
   * setting, so raising it changes how many routes their zone generates.
   */
  const handleSaveCapacity = useCallback(
    async (value: string) => {
      if (!selectedDriverUid || !selectedSchedule) return;
      const capacity = Number(value);
      if (!Number.isFinite(capacity) || capacity < 1) return;
      if (capacity === selectedSchedule.vehicle_capacity) return;

      try {
        await updateScheduleMutation.mutateAsync({
          driverUid: selectedDriverUid,
          params: { vehicleCapacity: Math.round(capacity) },
        });
        refetchSchedules();
      } catch (error) {
        console.error('Failed to update vehicle capacity:', error);
      }
    },
    [selectedDriverUid, selectedSchedule, updateScheduleMutation, refetchSchedules]
  );

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Driver Schedules</h1>
          <p className="text-gray-500 text-sm">
            Manage driver availability and working hours
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">
            {schedules?.length || 0} schedules • {drivers?.length || 0} drivers
          </div>
        </div>
      </div>

      {/* Error State */}
      {isError && (
        <div className="p-4 bg-red-50 border-b border-red-200 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-800">Failed to load data</p>
          <Button variant="outline" size="sm" onClick={() => refetchSchedules()}>
            Retry
          </Button>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Driver List */}
        <div className="w-96 border-r border-gray-200 bg-gray-50 flex flex-col">
          {/* Search and Filter */}
          <div className="p-3 border-b border-gray-200 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search drivers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showOnlyWithSchedule ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setShowOnlyWithSchedule(!showOnlyWithSchedule)}
              >
                With Schedule
              </Button>
              <span className="text-xs text-gray-500">
                {filteredDrivers.length} drivers
              </span>
            </div>
          </div>

          {/* Driver List */}
          <div className="flex-1 overflow-y-auto p-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : filteredDrivers.length === 0 ? (
              <Card>
                <div className="text-center py-8">
                  <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No drivers found</h3>
                  <p className="text-gray-500 text-sm">
                    {searchQuery ? 'Try a different search term' : 'No drivers available'}
                  </p>
                </div>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredDrivers.map(({ driver, schedule }) => {
                  const isSelected = driver.driver_uid === selectedDriverUid;
                  const hasSchedule = !!schedule;
                  const available = isAvailableToday(schedule);

                  return (
                    <Card
                      key={driver.driver_uid}
                      padding="sm"
                      className={cn(
                        'cursor-pointer transition-all hover:shadow-md',
                        isSelected && 'ring-2 ring-blue-500 bg-blue-50'
                      )}
                      onClick={() => setSelectedDriverUid(driver.driver_uid)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <div
                          className={cn(
                            'w-10 h-10 rounded-full flex items-center justify-center',
                            hasSchedule
                              ? available ? 'bg-green-100' : 'bg-orange-100'
                              : 'bg-gray-100'
                          )}
                        >
                          <User
                            className={cn(
                              'h-5 w-5',
                              hasSchedule
                                ? available ? 'text-green-600' : 'text-orange-500'
                                : 'text-gray-400'
                            )}
                          />
                        </div>

                        {/* Driver Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <span dir="auto" className="font-medium text-gray-900 break-words min-w-0">
                              {driver.full_name || driver.driver_uid}
                            </span>
                            {hasSchedule ? (
                              <Badge
                                variant={schedule.is_active ? 'success' : 'default'}
                                size="sm"
                              >
                                {schedule.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            ) : (
                              <Badge variant="warning" size="sm">
                                No Schedule
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {getTodayHours(schedule)}
                            </span>
                            {hasSchedule && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {getWorkingDays(schedule)} days/week
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Availability Indicator */}
                        <div className="flex-shrink-0">
                          {hasSchedule ? (
                            available ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-orange-400" />
                            )
                          ) : (
                            <Plus className="h-5 w-5 text-gray-300" />
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Schedule Details */}
        <div className="flex-1 overflow-y-auto p-4">
          {selectedDriver ? (
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Driver Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedDriver.full_name || selectedDriver.driver_uid}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {selectedSchedule ? (
                      <>
                        {selectedSchedule.is_active ? 'Active' : 'Inactive'} •
                        Capacity: {selectedSchedule.vehicle_capacity || selectedDriver.vehicle_capacity || 'Not set'}
                      </>
                    ) : (
                      'No schedule configured'
                    )}
                  </p>
                </div>
                {can('schedules.manage') && (!selectedSchedule ? (
                  <Button
                    variant="primary"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => handleCreateSchedule(selectedDriver.driver_uid)}
                    loading={createScheduleMutation.isPending}
                  >
                    Create Schedule
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    leftIcon={<Trash2 className="h-4 w-4" />}
                    onClick={handleDeleteSchedule}
                    loading={deleteScheduleMutation.isPending}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Delete Schedule
                  </Button>
                ))}
              </div>

              {/* Schedule Content */}
              {selectedSchedule ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Zone Assignment */}
                    <Card>
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin className="h-5 w-5 text-gray-500" />
                        <h3 className="font-medium text-gray-900">Service Zones</h3>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        Select zones this driver can service. Tasks in these zones will be assigned to this driver.
                      </p>
                      {zones && zones.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {zones.map((zone: Zone) => {
                            const isAssigned = selectedSchedule.zone_ids?.includes(zone._id) || false;
                            return (
                              <button
                                key={zone._id}
                                onClick={() => handleZoneToggle(zone._id)}
                                disabled={!can('schedules.manage')}
                                className={cn(
                                  'px-3 py-1.5 rounded-full text-sm font-medium transition-all border-2',
                                  isAssigned
                                    ? 'text-white border-transparent'
                                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                )}
                                style={isAssigned ? { backgroundColor: zone.color, borderColor: zone.color } : {}}
                              >
                                {zone.name}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No zones configured</p>
                      )}
                      {selectedSchedule.zone_ids && selectedSchedule.zone_ids.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          {selectedSchedule.zone_ids.length} zone{selectedSchedule.zone_ids.length > 1 ? 's' : ''} assigned
                        </p>
                      )}
                    </Card>

                    {/* Vehicle capabilities */}
                    <Card>
                      <div className="flex items-center gap-2 mb-3">
                        <Snowflake className="h-5 w-5 text-gray-500" />
                        <h3 className="font-medium text-gray-900">Vehicle</h3>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        Mark this driver as running a refrigerated van. Routes carrying
                        temperature-controlled orders can then only be assigned to a driver marked
                        here.
                      </p>
                      <button
                        onClick={() => handleCapabilityToggle(COLD_CHAIN_CAPABILITY)}
                        disabled={updateScheduleMutation.isPending || !can('schedules.manage')}
                        className={cn(
                          'px-3 py-1.5 rounded-full text-sm font-medium transition-all border-2 disabled:opacity-50',
                          selectedSchedule.capabilities?.includes(COLD_CHAIN_CAPABILITY)
                            ? 'bg-sky-600 text-white border-sky-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                        )}
                      >
                        Refrigerated van
                      </button>
                      {!selectedSchedule.capabilities?.includes(COLD_CHAIN_CAPABILITY) && (
                        <p className="text-xs text-gray-500 mt-2">
                          While no driver anywhere is marked, cold routes accept any driver — the
                          guard would otherwise leave them unassignable.
                        </p>
                      )}

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <label
                          htmlFor="home-zone-label"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Home zone label
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5 mb-2">
                          Where ops considers this vehicle based. A note for people — it is not a
                          zone and does not constrain routing.
                        </p>
                        <Input
                          id="home-zone-label"
                          key={`hz-${selectedDriverUid}-${selectedSchedule.home_zone_label ?? ''}`}
                          defaultValue={selectedSchedule.home_zone_label ?? ''}
                          placeholder="e.g. TempZone-1"
                          maxLength={60}
                          disabled={updateScheduleMutation.isPending || !can('schedules.manage')}
                          onBlur={(e) => handleSaveHomeZoneLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <label
                          htmlFor="vehicle-capacity"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Vehicle capacity
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5 mb-2">
                          Orders this driver can carry on one route. Applies to{' '}
                          <strong>every shift they work</strong>, not just cold — raising it changes
                          how many routes their zone generates.
                        </p>
                        <Input
                          id="vehicle-capacity"
                          type="number"
                          min={1}
                          key={`cap-${selectedDriverUid}-${selectedSchedule.vehicle_capacity ?? ''}`}
                          defaultValue={selectedSchedule.vehicle_capacity ?? ''}
                          disabled={updateScheduleMutation.isPending || !can('schedules.manage')}
                          onBlur={(e) => handleSaveCapacity(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                      </div>
                    </Card>

                    {/* Weekly Schedule */}
                    <WeeklyScheduleGrid
                      schedule={selectedSchedule.weekly_template}
                      onSave={handleSaveWeeklySchedule}
                      isSaving={updateScheduleMutation.isPending}
                    />

                    {/* Exceptions */}
                    <ScheduleExceptionForm
                      exceptions={selectedSchedule.exceptions}
                      onAddException={handleAddException}
                      onRemoveException={handleRemoveException}
                      isLoading={addExceptionMutation.isPending}
                    />
                  </div>

                  {/* Right Column */}
                  <div>
                    {/* Calendar */}
                    <AvailabilityCalendar
                      weeklySchedule={selectedSchedule.weekly_template}
                      exceptions={selectedSchedule.exceptions}
                    />
                  </div>
                </div>
              ) : (
                <Card className="p-8 text-center">
                  <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedule Yet</h3>
                  <p className="text-gray-500 text-sm mb-4">
                    This driver doesn't have a schedule configured. Create one to set their working hours.
                  </p>
                  {can('schedules.manage') && (
                    <Button
                      variant="primary"
                      leftIcon={<Plus className="h-4 w-4" />}
                      onClick={() => handleCreateSchedule(selectedDriver.driver_uid)}
                      loading={createScheduleMutation.isPending}
                    >
                      Create Schedule
                    </Button>
                  )}
                </Card>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-medium">Select a driver</p>
                <p className="text-sm">Choose a driver from the list to view and edit their schedule</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
